// Columna central del explorer: las 11 columnas ordenables del diseño, pintadas con el `DataTable`
// compartido. Sin filas NO pinta una tabla vacía —que se leería como «no hay créditos»— sino el motivo.
import { useMemo, type ReactNode } from 'react';
import { formatShortDateMx } from '@/lib/dateHelpers';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { OutlinePill, StatusPill, TierPill, TONE_OUTLINE_CLASS } from '@/shared/patterns/Pills';
import { CREDIT_STATUS_PRESENTATION, formatMoneyCents } from '../../creditFile/logic';
import {
    arrearsCell,
    balanceCell,
    capitalSubLine,
    couponCell,
    levelCell,
    lockCell,
    MISSING_COLUMN_CELL,
    TOTAL_DEBT_COLUMN_NOTE,
    totalDebtCell,
} from '../format';
import { nextSort, rowLockReading } from '../logic';
import type { PortfolioRow, PortfolioSort, PortfolioSortKey } from '../types';

/**
 * Las 10 columnas fijas suman 838px y Cliente pide 142 más. Por debajo de ese mínimo la tabla
 * scrollea dentro de su panel, en vez de comprimir las últimas columnas hasta partirlas.
 */
const TABLE_MIN_WIDTH = '980px';

/** Por qué la tabla no tiene filas que pintar. Nunca una tabla vacía, que diría «no hay créditos». */
export interface PortfolioUnavailable {
    title: string;
    body: string;
}

/** Lo que pinta una columna que puede no tener fuente: el valor, o el token del ausente. */
function missingAwareCell(value: string | undefined): ReactNode {
    return (
        value ?? (
            <span className="font-normal italic text-muted-foreground">{MISSING_COLUMN_CELL}</span>
        )
    );
}

/**
 * El `title` de esa celda. `noteAsTitle` es cuando el valor pintado es un sustituto —el id crudo de
 * la tienda o del cliente—: ahí manda el motivo, que es lo único que explica ese id.
 */
function missingAwareTitle(
    value: string | undefined,
    note: string,
    noteAsTitle: boolean,
): string | undefined {
    return noteAsTitle ? note : (value ?? note);
}

/** Las 11 columnas del diseño, con SUS anchos en px. Cliente es la flexible y se queda el resto. */
const COLUMNS: DataTableColumn<PortfolioRow, PortfolioSortKey>[] = [
    {
        key: 'credit',
        label: 'CR',
        width: '100px',
        sortKey: 'credit',
        cell: (row) => (
            <>
                {/* eslint-disable-next-line no-restricted-syntax -- columna «CR»: el silo no emite folio legible y un CopyableId no cabe en la celda */}
                <div className="truncate font-mono text-[11px] leading-[17px]">{row.creditId}</div>
                <div className="text-[9.5px] leading-[14px] tabular-nums text-muted-foreground">
                    {formatShortDateMx(row.createdAt)}
                </div>
            </>
        ),
    },
    {
        key: 'level',
        label: 'Nivel',
        width: '60px',
        sortKey: 'level',
        cellOverflow: 'nowrap',
        cell: (row) => (
            <TierPill level={row.clientLevel ?? null} title={levelCell(row.clientLevel).title} />
        ),
    },
    {
        key: 'customer',
        label: 'Cliente',
        sortKey: 'customer',
        cell: (row) => missingAwareCell(row.customerName),
        cellTitle: (row) =>
            missingAwareTitle(row.customerName, row.customerNote, !row.customerNameResolved),
        cellClassName: () => 'font-medium',
    },
    {
        key: 'product',
        label: 'Producto',
        width: '112px',
        sortKey: 'product',
        cell: (row) => missingAwareCell(row.productLabel),
        cellTitle: (row) => missingAwareTitle(row.productLabel, row.productNote, false),
    },
    {
        key: 'store',
        label: 'Tienda',
        width: '120px',
        sortKey: 'store',
        cell: (row) => missingAwareCell(row.storeName),
        cellTitle: (row) => missingAwareTitle(row.storeName, row.storeNote, !row.storeNameResolved),
    },
    {
        key: 'payment',
        label: 'Cuota',
        width: '76px',
        numeric: true,
        sortKey: 'payment',
        cellOverflow: 'wrap',
        cell: (row) => formatMoneyCents(row.weeklyPaymentWithIvaCents),
    },
    {
        key: 'progress',
        label: 'Cup',
        width: '60px',
        sortKey: 'progress',
        cellOverflow: 'wrap',
        cell: (row) => {
            const coupon = couponCell(row.paidInstallments, row.termWeeks);
            return (
                <>
                    <div className="text-[10.5px] leading-tight tabular-nums">{coupon.label}</div>
                    <div className="mt-[2px] h-[3px] overflow-hidden rounded-[2px] bg-muted">
                        <div
                            className="h-full bg-success"
                            style={{ width: `${String(coupon.progressPct)}%` }}
                        />
                    </div>
                </>
            );
        },
    },
    {
        key: 'balance',
        label: 'Saldo',
        width: '92px',
        numeric: true,
        sortKey: 'balance',
        cellOverflow: 'wrap',
        cell: (row) => balanceCell(row.outstandingBalanceCents, row.status).label,
        cellTitle: (row) => balanceCell(row.outstandingBalanceCents, row.status).title,
    },
    {
        key: 'status',
        label: 'Estado',
        width: '76px',
        sortKey: 'status',
        cellOverflow: 'nowrap',
        cell: (row) => {
            const status = CREDIT_STATUS_PRESENTATION[row.status];
            return <StatusPill tone={status.tone}>{status.shortLabel}</StatusPill>;
        },
    },
    {
        key: 'arrears',
        label: 'Mora',
        width: '62px',
        sortKey: 'arrears',
        cellOverflow: 'nowrap',
        cell: (row) => {
            const arrears = arrearsCell(row.arrearsDays, row.status);
            return (
                <StatusPill tone={arrears.tone} title={arrears.title}>
                    {arrears.label}
                </StatusPill>
            );
        },
    },
    {
        key: 'lock',
        label: 'Bloqueo',
        width: '80px',
        sortKey: 'lock',
        cellOverflow: 'nowrap',
        cell: (row) => {
            const lock = lockCell(
                row.lockState,
                rowLockReading(row),
                row.lockEvidence?.currentLockScheduledFor,
            );
            return (
                <OutlinePill outline={TONE_OUTLINE_CLASS[lock.tone]} title={lock.title}>
                    {lock.label}
                </OutlinePill>
            );
        },
    },
];

/** La deuda total como cifra principal y el capital insoluto debajo, en chico. */
const TOTAL_DEBT_COLUMN: DataTableColumn<PortfolioRow, PortfolioSortKey> = {
    key: 'balance',
    label: 'Deuda total',
    width: '92px',
    numeric: true,
    sortKey: 'balance',
    note: TOTAL_DEBT_COLUMN_NOTE,
    cellOverflow: 'wrap',
    cell: (row) => {
        const capital = capitalSubLine(row.outstandingBalanceCents, row.status);
        return (
            <>
                <div>{totalDebtCell(row.totalDebtCents, row.status).label}</div>
                {capital === null ? null : (
                    <div className="text-[9.5px] leading-[14px] text-muted-foreground">
                        {capital}
                    </div>
                )}
            </>
        );
    },
    cellTitle: (row) => totalDebtCell(row.totalDebtCents, row.status).title,
};

/** Con alguna fila que traiga la deuda total, la columna «Saldo» pasa a ser «Deuda total». */
export function hasTotalDebt(rows: readonly PortfolioRow[] | null): boolean {
    return rows?.some((row) => row.totalDebtCents !== undefined) ?? false;
}

function columnsFor(withTotalDebt: boolean): DataTableColumn<PortfolioRow, PortfolioSortKey>[] {
    if (!withTotalDebt) return COLUMNS;
    return COLUMNS.map((column) => (column.key === 'balance' ? TOTAL_DEBT_COLUMN : column));
}

export interface PortfolioTableProps {
    /** `null` = todavía no hay filas que pintar. Distinto de un arreglo vacío por los filtros. */
    rows: readonly PortfolioRow[] | null;
    /** Qué decir cuando `rows` es `null`: si está cargando o por qué falló la lectura. */
    unavailable: PortfolioUnavailable;
    sort: PortfolioSort;
    onSortChange: (next: PortfolioSort) => void;
    selectedCreditId: string | null;
    onSelect: (creditId: string) => void;
    className?: string;
}

export function PortfolioTable({
    rows,
    unavailable,
    sort,
    onSortChange,
    selectedCreditId,
    onSelect,
    className,
}: PortfolioTableProps) {
    const withTotalDebt = hasTotalDebt(rows);
    const columns = useMemo(() => columnsFor(withTotalDebt), [withTotalDebt]);
    return (
        <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.creditId}
            minWidth={TABLE_MIN_WIDTH}
            caption={`Créditos de la cartera del tenant, con su nivel, cuota, avance, ${withTotalDebt ? 'deuda total' : 'saldo'}, estado, mora y bloqueo.`}
            sort={sort}
            onSortToggle={(key) => {
                onSortChange(nextSort(sort, key));
            }}
            isRowSelected={(row) => row.creditId === selectedCreditId}
            onRowClick={(row) => {
                onSelect(row.creditId);
            }}
            empty="Ningún crédito coincide con los filtros."
            unavailable={<UnavailableState {...unavailable} />}
            className={className}
        />
    );
}
